import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useCanWrite } from "@/app/auth";
import { ClearFilters } from "@/app/ClearFilters";
import { accounts, customers } from "@/app/master";
import { Pager } from "@/app/Pager";
import { accountOptions, idOptions, loadingOf, nameOf, useList } from "@/app/queries";
import { accountRouting, routingRules, routingTemplates } from "@/app/routing";
import { usePage } from "@/app/usePage";
import { useSearchFilters } from "@/app/useSearchFilters";
import { Alert, Button, Combobox, EmptyState, FILTER_W, PageHeader, Pill, Table, TableWrap, Th, Toolbar } from "@/ui";

/** 独立账号路由列表：按客户和账号定位绑定，编辑沿用账号路由版本保护（docs/04）。 */
export function AccountRoutes() {
  const [params, setParams] = useSearchParams();
  const canWrite = useCanWrite(), location = useLocation();
  const accountList = useList("accounts", accounts), customerList = useList("customers", customers);
  const { filters, set, clear, active } = useSearchFilters(["customer", "account"]);
  const ownAccounts = (accountList.data ?? []).filter(a => !filters.customer || String(a.customer_id) === filters.customer);
  const rows = ownAccounts.filter(a => !filters.account || String(a.id) === filters.account);
  const page = usePage("account-routes", rows, filters);
  const error = accountList.error ?? customerList.error;
  return <>
    <PageHeader title="账号路由" description="各账号按顺序匹配的路由规则与模板；没有绑定的账号发送返回无路由（012）。" />
    {error && <Alert tone="danger" title="加载失败">{error.message}</Alert>}
    <Toolbar actions={active && <ClearFilters onClear={clear} />}>
      <Combobox size="sm" width={FILTER_W.select} ariaLabel="客户" searchPlaceholder="搜索客户名称" options={[{ value: "", label: "全部客户" }, ...idOptions(customerList.data)]} value={filters.customer} onChange={v => { const next = new URLSearchParams(params); next.delete("account"); if (v) next.set("customer", v); else next.delete("customer"); setParams(next, { replace: true }); }} />
      <Combobox size="sm" width={FILTER_W.select} ariaLabel="账号" searchPlaceholder="搜索账号名称或 system_id" options={[{ value: "", label: "全部账号" }, ...accountOptions(ownAccounts, customerList.data)]} value={filters.account} onChange={v => set("account", v)} />
    </Toolbar>
    <TableWrap loading={loadingOf(accountList)}>{page.rows.length ? <>
      <Table><thead><tr><Th w="name">账号</Th><Th w="name">客户</Th><Th w={360} title="按匹配顺序列出；名字后带 * 的是路由模板，括号里是优先级">绑定规则或模板（优先级）</Th><Th>操作</Th></tr></thead><tbody>
        {page.rows.map(a => <tr key={a.id}>
          {/* 账号本身的状态与绑定无关，不单设一列；只在账号停用时在名字后标出，避免与绑定的启用混淆（#363）。 */}
          <td>{a.name}{a.status === "disabled" && <> <Pill tone="warning">已停用</Pill></>}</td>
          <td>{nameOf(customerList.data, a.customer_id)}</td>
          <td><BindingSummary accountId={a.id} /></td>
          <td><Link className="ax-btn ax-btn-ghost ax-btn-sm" to={`/account-routing/${a.id}${location.search}`}>{canWrite ? "配置绑定" : "查看绑定"}</Link></td>
        </tr>)}
      </tbody></Table><Pager {...page} />
    </> : !error && !accountList.isPending && <EmptyState title={active ? "没有符合筛选条件的账号" : "还没有账号"}>请先在“客户账号”创建账号，再配置路由绑定。</EmptyState>}</TableWrap>
  </>;
}

// 仅为当前页账号读取绑定，翻页卸载，避免一次请求所有账号的配置。
function BindingSummary({ accountId }: { accountId: number }) {
  const record = useQuery({ queryKey: ["account-routing", accountId], queryFn: () => accountRouting.get(accountId) });
  const rules = useQuery({ queryKey: ["routing-rules"], queryFn: () => routingRules.list() });
  const templates = useQuery({ queryKey: ["routing-templates"], queryFn: () => routingTemplates.list() });
  if (record.error) return <span>绑定读取失败 <Button size="sm" variant="ghost" onClick={() => void record.refetch()}>重试</Button></span>;
  if (!record.data) return <span className="ax-secondary">加载中</span>;
  if (!record.data.bindings.length) return <span className="ax-secondary">未绑定 · 无路由（012）</span>;
  return <>{(rules.error || templates.error) && <div className="ax-secondary">规则或模板名称读取失败，以 ID 展示</div>}{[...record.data.bindings].sort((a, b) => a.priority - b.priority).map(b => <div key={b.id}>{b.target_type === "template"
    // 模板名后加 *：星号对读屏隐藏，另给视觉隐藏的"（路由模板）"，含义不只靠星号（#363）。
    ? <>{nameOf(templates.data, b.template_id ?? 0)}<span aria-hidden="true">*</span><span className="ax-sr-only">（路由模板）</span></>
    : nameOf(rules.data, b.rule_id ?? 0)}（{b.priority}）{!b.enabled && " · 停用"}</div>)}</>;
}
